import React, { useEffect, useRef, useState } from "react";

/**
 * SakuraPetalsCanvas:
 * Renders gentle, peaceful falling Japanese sakura (cherry blossom) petals.
 * Lightweight, GPU-accelerated canvas animation with zero external dependencies.
 * Automatically respects reduced-motion preferences.
 */
export default function SakuraPetalsCanvas({ count = 18, speed = 0.45, className = "" }) {
  const canvasRef = useRef(null);
  const [isReducedMotion, setIsReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setIsReducedMotion(mediaQuery.matches);

    const handleMotionChange = (e) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handleMotionChange);

    return () => mediaQuery.removeEventListener("change", handleMotionChange);
  }, []);

  useEffect(() => {
    if (isReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const updateSize = () => {
      if (!canvas) return { w: window.innerWidth, h: window.innerHeight };
      const w = canvas.offsetWidth || canvas.clientWidth || window.innerWidth;
      const h = canvas.offsetHeight || canvas.clientHeight || window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };

    let { w: width, h: height } = updateSize();

    const handleResize = () => {
      const size = updateSize();
      if (size) {
        width = size.w;
        height = size.h;
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // ResizeObserver to track container height changes dynamically
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(canvas.parentElement);
    }

    // Dynamic petal count scaling with screen width for uniform coverage across all screen sizes
    const targetCount = count || Math.max(20, Math.floor(width / 60));

    // Sakura Petal Particle Definition
    class Petal {
      constructor() {
        this.reset(true);
      }

      reset(init = false) {
        // Spawn across the entire screen width edge-to-edge
        this.x = Math.random() * (width + 80) - 40;
        this.y = init ? Math.random() * height : -30 - Math.random() * 50;
        this.size = 9 + Math.random() * 11;
        this.speedY = (0.4 + Math.random() * 0.6) * speed;
        this.speedX = (-0.3 + Math.random() * 0.7) * speed;
        this.rotation = Math.random() * 360;
        this.rotSpeed = (Math.random() - 0.5) * 0.9;
        this.flip = Math.random() * 360;
        this.flipSpeed = 0.012 + Math.random() * 0.022;
        this.opacity = 0.45 + Math.random() * 0.4;
        this.swaySpeed = 0.007 + Math.random() * 0.013;
        this.swayOffset = Math.random() * Math.PI * 2;

        const colors = [
          { r: 255, g: 218, b: 228 }, // Delicate blush pink
          { r: 247, g: 194, b: 209 }, // Classic sakura blossom
          { r: 240, g: 168, b: 190 }, // Richer petal tone
          { r: 255, g: 235, b: 242 }, // Light petal highlight
          { r: 236, g: 154, b: 180 }, // Rose accent
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.y += this.speedY;
        this.x += this.speedX + Math.sin(this.swayOffset) * 0.55;
        this.swayOffset += this.swaySpeed;
        this.rotation += this.rotSpeed;
        this.flip += this.flipSpeed;

        if (this.y > height + 40 || this.x > width + 60 || this.x < -60) {
          this.reset(false);
        }
      }

      draw(context) {
        context.save();
        context.translate(this.x, this.y);
        context.rotate((this.rotation * Math.PI) / 180);
        context.scale(1, Math.sin(this.flip));

        // Draw classic curved Sakura petal shape with delicate notch
        context.beginPath();
        const r = this.size;

        context.moveTo(0, -r);
        // Right side curve with soft heart/notch flare
        context.bezierCurveTo(r * 0.85, -r * 0.85, r * 0.95, r * 0.35, 0, r);
        // Left side curve
        context.bezierCurveTo(-r * 0.95, r * 0.35, -r * 0.85, -r * 0.85, 0, -r);

        const gradient = context.createRadialGradient(0, -r * 0.2, 1, 0, 0, r);
        gradient.addColorStop(
          0,
          `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 1.15})`
        );
        gradient.addColorStop(
          0.75,
          `rgba(${this.color.r - 8}, ${this.color.g - 18}, ${this.color.b - 14}, ${this.opacity})`
        );
        gradient.addColorStop(
          1,
          `rgba(${this.color.r - 20}, ${this.color.g - 30}, ${this.color.b - 22}, ${this.opacity * 0.75})`
        );

        context.fillStyle = gradient;
        context.shadowColor = "rgba(224, 136, 158, 0.25)";
        context.shadowBlur = 4;
        context.fill();

        // Delicate central petal vein
        context.beginPath();
        context.moveTo(0, -r * 0.75);
        context.lineTo(0, r * 0.65);
        context.strokeStyle = `rgba(255, 255, 255, ${this.opacity * 0.5})`;
        context.lineWidth = 0.75;
        context.stroke();

        context.restore();
      }
    }

    const petals = Array.from({ length: targetCount }, () => new Petal());

    const render = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < petals.length; i++) {
        petals[i].update();
        petals[i].draw(ctx);
      }
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      cancelAnimationFrame(animationFrameId);
    };
  }, [count, speed, isReducedMotion]);

  if (isReducedMotion) {
    return null;
  }

  const isAbsolute = className.includes("absolute");

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none ${className || "fixed inset-0 top-0 left-0 w-full h-full z-0 max-w-full"}`}
      style={isAbsolute ? {} : { width: "100%", height: "100%", position: "fixed", top: 0, left: 0 }}
      aria-hidden="true"
    />
  );
}
